import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, TextField, Divider, Chip, List, ListItem, ListItemText, Autocomplete, Button, ButtonGroup, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Collapse, Badge, Drawer } from '@mui/material';
import { styled } from '@mui/material/styles';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CalendarPanel from './CalendarPanel';
import EditBookingModal from './EditBookingModal';
import ItineraryModal from './ItineraryModal';
import { API_BASE_URL } from '../config';
import { apiGet, apiPost, apiDelete } from '../utils/api';
import { authaltFetch, AUTHALT_API_BASE } from '../services/authaltApi';
import {
  formatCompactTherapistDisplay,
  getTherapistDisplayTitle,
} from '../utils/therapistDisplay';

// API Configuration - token fetched dynamically from backend
const GUEST_API_BASE = AUTHALT_API_BASE;

const ViewButton = styled(Button)(({ selected }) => ({
  textTransform: 'none',
  minWidth: '120px',
  backgroundColor: selected ? '#E4C984' : 'transparent',
  color: '#171717',
  '&:hover': { backgroundColor: selected ? '#cbb16f' : 'rgba(0,0,0,0.04)' },
  borderColor: '#E4C984',
  fontWeight: 700
}));

const GoldButtonGroup = styled(ButtonGroup)(() => ({
  '& .MuiButton-outlined': { borderColor: '#E4C984' },
  '& .MuiButton-outlined:hover': { borderColor: '#cbb16f', backgroundColor: 'rgba(228, 201, 132, 0.12)' },
  '& .MuiButtonBase-root.Mui-focusVisible': { boxShadow: '0 0 0 2px rgba(228, 201, 132, 0.6)' }
}));

const formatDateISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const renderBookingTherapistChip = (booking) => {
  const label = formatCompactTherapistDisplay(booking);
  if (!label) return null;
  return (
    <Chip
      size="small"
      label={label}
      title={getTherapistDisplayTitle(booking)}
      sx={{
        height: 22,
        fontSize: '0.72rem',
        fontWeight: 600,
        bgcolor: '#dcfce7',
        color: '#166534',
        border: '1px solid #86efac',
        alignSelf: 'flex-start',
      }}
    />
  );
};

const addDaysISO = (iso, offset) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + offset);
  return formatDateISO(dt);
};

const formatDisplayDateRange = (days) => {
  if (!days.length) return '';
  const parseLocal = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const first = parseLocal(days[0]);
  const last = parseLocal(days[days.length - 1]);
  const fmt = (dt, withYear) =>
    dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', ...(withYear ? { year: 'numeric' } : {}) });
  if (days.length === 1) return fmt(first, true);
  return `${fmt(first, false)} - ${fmt(last, true)}`;
};

const guestIdsMatch = (a, b) => {
  if (a == null || b == null || a === '' || b === '') return false;
  return String(a) === String(b);
};

const DoctorDesk = ({ externalMode = false, lockedGuestId = null, lockedGuestName = null, doctorContext = null, onUnauthorized = null }) => {
  const [activeView, setActiveView] = useState('desk');
  const [date, setDate] = useState(() => formatDateISO(new Date()));
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [guestQuery, setGuestQuery] = useState('');
  const [roomQuery, setRoomQuery] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingsByDate, setBookingsByDate] = useState({});
  const [config, setConfig] = useState({ rooms: {}, treatments: [], therapists: {}, equipment: {} });
  const [calendarDay, setCalendarDay] = useState(() => formatDateISO(new Date()));
  const [selectedBooking, setSelectedBooking] = useState(null);
  const enrichBookingForEdit = (booking, scheduleDate) => ({
    ...booking,
    date: scheduleDate || booking.date,
    guestGender: selectedGuest?.gender || booking.guestGender || booking.guest_gender || booking.gender,
    gender: selectedGuest?.gender || booking.gender,
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [guests, setGuests] = useState([]);
  const [guestLookupReady, setGuestLookupReady] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [externalGuestLoaded, setExternalGuestLoaded] = useState(false);
  const [availableActivities, setAvailableActivities] = useState([]);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [dayFilter, setDayFilter] = useState('today'); // 'today', 'tomorrow', 'next7days', 'all'
  const [timelineViewMode, setTimelineViewMode] = useState('normal'); // 'normal' or 'compact'
  const [sidePanelsCollapsed, setSidePanelsCollapsed] = useState(false); // Collapse both Doctor Desk and Results panels
  const [availableActivitiesByDate, setAvailableActivitiesByDate] = useState({});
  const [showAvailableActivitiesModal, setShowAvailableActivitiesModal] = useState(false);
  const [selectedSlotInfo, setSelectedSlotInfo] = useState(null); // { date, time, activities }
  const [activityDurations, setActivityDurations] = useState({}); // Track duration for each activity by ID
  const [guestFilter, setGuestFilter] = useState(() => localStorage.getItem('doctorDeskGuestFilter') || 'inhouse'); // 'upcoming' or 'inhouse'
  const [allGuests, setAllGuests] = useState([]); // All fetched guests
  const [apiCounts, setApiCounts] = useState({ inHouse: 0, upcoming: 0 }); // Store API-provided counts
  const [showItinerary, setShowItinerary] = useState(false);
  
  // Filter panel state
  const [filterPanelOpen, setFilterPanelOpen] = useState(() => localStorage.getItem('doctorDeskFilterPanelOpen') === 'true');
  const [advancedFilters, setAdvancedFilters] = useState({
    gender: [],
    guestTag: [],
    contactType: [],
    package: [],
    ageRange: [],
    diet: [],
    duration: [],
    consultingDoctor: []
  });
  const [expandedFilters, setExpandedFilters] = useState({
    gender: true,
    guestTag: true,
    contactType: false,
    package: true,
    ageRange: false,
    diet: false,
    duration: false,
    consultingDoctor: false
  });

  useEffect(() => {
    localStorage.setItem('doctorDeskGuestFilter', guestFilter);
  }, [guestFilter]);

  useEffect(() => {
    localStorage.setItem('doctorDeskFilterPanelOpen', String(filterPanelOpen));
  }, [filterPanelOpen]);

  // Auto-select guest when in external mode
  useEffect(() => {
    if (externalMode && lockedGuestId && !externalGuestLoaded) {
      // Fetch guest details by ID
      const loadExternalGuest = async () => {
        try {
          const res = await apiGet(`/api/guests?search=${encodeURIComponent(lockedGuestId)}`);
          if (res.ok) {
            const data = await res.json();
            const guests = data.guests || data || [];
            const matchingGuest = guests.find(g => g.id === lockedGuestId || g.guestId === lockedGuestId);
            if (matchingGuest) {
              setSelectedGuest(matchingGuest);
              setGuestQuery(matchingGuest.name || lockedGuestId);
              setExternalGuestLoaded(true);
            }
          }
        } catch (err) {
          console.error('Failed to load external guest:', err);
        }
      };
      loadExternalGuest();
    }
  }, [externalMode, lockedGuestId, externalGuestLoaded]);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await apiGet('/api/config');
        const data = await res.json();
        setConfig({
          rooms: data.rooms || {},
          treatments: data.treatments || [],
          therapists: data.therapists || {},
          equipment: data.equipment || {}
        });
      } catch {}
    };
    loadConfig();
  }, []);

  // Helper functions for guest filtering
  const getFirstNonEmpty = (obj, keys) => {
    for (const k of keys) {
      const v = obj[k];
      if (v !== null && v !== undefined && String(v).trim() !== '') return v;
    }
    return null;
  };

  const toISO = (s) => {
    if (!s) return null;
    const str = String(s).trim();
    // Support YYYY-MM-DD first
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    // Try DD/MM/YYYY or DD-MM-YYYY
    const m = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (m) {
      const d = String(m[1]).padStart(2,'0');
      const mo = String(m[2]).padStart(2,'0');
      const y = String(m[3]).length === 2 ? `20${m[3]}` : String(m[3]);
      return `${y}-${mo}-${d}`;
    }
    // Fallback Date parse
    const t = Date.parse(str);
    if (!Number.isNaN(t)) return new Date(t).toISOString().split('T')[0];
    return null;
  };

  const getCheckInISO = (g) => toISO(getFirstNonEmpty(g, ['checkIn', 'checkin', 'check_in', 'startDate', 'start', 'arrivalDate', 'ArrivalDate']));
  const getCheckOutISO = (g) => toISO(getFirstNonEmpty(g, ['checkOut', 'checkout', 'check_out', 'endDate', 'end', 'departureDate', 'DepartureDate']));

  const formatDateRange = (g) => {
    const ci = getCheckInISO(g);
    const co = getCheckOutISO(g);
    if (!ci || !co) return '';
    
    const formatDate = (isoDate) => {
      const date = new Date(isoDate);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    return `${formatDate(ci)} - ${formatDate(co)}`;
  };

  const isInHouse = (g, d) => {
    const ci = getCheckInISO(g); 
    const co = getCheckOutISO(g);
    if (!ci || !co) return false; 
    return ci <= d && d <= co;
  };

  const isUpcoming = (g, d) => {
    const ci = getCheckInISO(g); 
    if (!ci) return false; 
    return ci > d;
  };

  useEffect(() => {
    const syncCalendarDay = () => {
      const today = formatDateISO(new Date());
      setCalendarDay((prev) => (prev === today ? prev : today));
    };
    syncCalendarDay();
    const intervalId = window.setInterval(syncCalendarDay, 60_000);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncCalendarDay();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Helper function to get age range
  const getAgeRange = (age) => {
    if (!age) return null;
    const ageNum = parseInt(age);
    if (isNaN(ageNum)) return null;
    if (ageNum < 20) return '< 20';
    if (ageNum < 30) return '20-30';
    if (ageNum < 40) return '30-40';
    if (ageNum < 50) return '40-50';
    if (ageNum < 60) return '50-60';
    return '60+';
  };

  // Check if guest matches advanced filters
  const matchesAdvancedFilters = useCallback((g) => {
    for (const [key, selectedValues] of Object.entries(advancedFilters)) {
      if (selectedValues.length === 0) continue;
      
      let guestValue;
      if (key === 'ageRange') {
        guestValue = getAgeRange(g.age || g.ageYears);
      } else if (key === 'package') {
        guestValue = String(g.program || g.package || '').trim();
      } else if (key === 'guestTag') {
        guestValue = String(g.tag || g.guestTag || '').trim();
      } else if (key === 'diet') {
        guestValue = String(g.diet || g.dietType || '').trim();
      } else if (key === 'duration') {
        guestValue = String(g.nights || '').trim();
      } else {
        guestValue = String(g[key] || '').trim();
      }
      
      if (!selectedValues.includes(guestValue)) {
        return false;
      }
    }
    return true;
  }, [advancedFilters]);

  // Load guests for autocomplete lookup
  useEffect(() => {
    const fetchGuests = async () => {
      try {
        // Fetch all guest types using the new API endpoints with POST
        const types = ['InHouse', 'TodaysArrival', 'TodaysDeparture', 'Next4Days'];
        let allGuestsData = [];
        let counts = { inHouse: 0, todaysArrival: 0, todaysDeparture: 0, arrivalNext4Days: 0 };
        
        for (const type of types) {
          try {
            const res = await authaltFetch('/guestlistdatav3', {
              type,
              index: 'atmantan'
            });
            if (res.ok) {
              const response = await res.json();
              const categoryKey = type === 'InHouse' ? 'inHouse' :
                                 type === 'TodaysArrival' ? 'todaysArrival' :
                                 type === 'TodaysDeparture' ? 'todaysDeparture' : 'arrivalNext4Days';
              const guestsFromType = response[categoryKey]?.data || [];
              counts[categoryKey] = response[categoryKey]?.count || 0;
              allGuestsData = allGuestsData.concat(guestsFromType);
            }
          } catch (e) {
            console.error(`Failed to fetch ${type}:`, e);
          }
        }
        
        // Store API counts
        setApiCounts({
          inHouse: counts.inHouse,
          upcoming: counts.todaysArrival + counts.arrivalNext4Days
        });
        
        // Remove duplicates by guestId
        const uniqueGuests = Array.from(
          new Map(allGuestsData.map(g => [g.guestId || g.id, g])).values()
        );
        
        // Normalize - keep ALL fields for filtering
        const list = uniqueGuests.map((g) => ({
          ...g, // Keep all original fields
          id: g.id || g.guestId || g.GuestId || '',
          name: g.name || g.guestName || g.firstName && g.lastName ? `${g.firstName} ${g.lastName}` : g.firstName || '',
          room: g.room || g.roomNo || g.location || '',
          consultingDoctor: g.consultingDoctor || g.doctor || '',
          checkIn: g.checkIn || g.startDate || g.arrivalDate || '',
          checkOut: g.checkOut || g.endDate || g.departureDate || '',
          // Normalize filter-relevant fields
          gender: g.gender || '',
          tag: g.tag || g.guestTag || '',
          guestTag: g.tag || g.guestTag || '',
          package: g.package || g.program || '',
          program: g.package || g.program || '',
          age: g.age || g.ageYears || '',
          ageYears: g.age || g.ageYears || '',
          contactType: g.contactType || '',
          diet: g.diet || g.dietType || '',
          dietType: g.diet || g.dietType || '',
          nights: g.nights || '',
        }));
        // Filter out guests without names and sort alphabetically by name
        const sorted = list
          .filter((g) => g.name && g.name.trim() !== '') // Only show guests with names
          .sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
        setAllGuests(sorted);
      } catch (e) {
        setAllGuests([]);
      } finally {
        setGuestLookupReady(true);
      }
    };
    fetchGuests();
  }, []);

  // Filter guests based on selected filter (upcoming/inhouse) and advanced filters
  useEffect(() => {
    let filtered = allGuests;
    
    // Apply guest status filter (upcoming/inhouse)
    if (guestFilter === 'upcoming') {
      filtered = filtered.filter(g => isUpcoming(g, calendarDay));
    } else if (guestFilter === 'inhouse') {
      filtered = filtered.filter(g => isInHouse(g, calendarDay));
    }
    
    // Apply advanced filters
    filtered = filtered.filter(matchesAdvancedFilters);
    
    setGuests(filtered);
  }, [guestFilter, allGuests, calendarDay, matchesAdvancedFilters]);

  const todayISO = calendarDay;
  const tomorrowISO = useMemo(() => addDaysISO(calendarDay, 1), [calendarDay]);
  const next7DaysISO = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) days.push(addDaysISO(calendarDay, i));
    return days;
  }, [calendarDay]);
  const dateRange = useMemo(() => {
    const range = [];
    // Forward-looking window from today (14 days)
    for (let i = 0; i <= 13; i++) range.push(addDaysISO(calendarDay, i));
    return range;
  }, [calendarDay]);
  const timelineDays = useMemo(() => {
    if (dayFilter === 'today') return [todayISO];
    if (dayFilter === 'tomorrow') return [tomorrowISO];
    if (dayFilter === 'next7days') return next7DaysISO;
    return dateRange;
  }, [dayFilter, todayISO, tomorrowISO, next7DaysISO, dateRange]);

  const guestScheduleDate = useMemo(() => {
    if (dayFilter === 'today') return todayISO;
    if (dayFilter === 'tomorrow') return tomorrowISO;
    if (date && timelineDays.includes(date)) return date;
    return timelineDays[0] || calendarDay;
  }, [dayFilter, todayISO, tomorrowISO, date, timelineDays, calendarDay]);

  const guestScheduleTitle = useMemo(() => {
    if (guestScheduleDate === calendarDay) return "Today's Schedule";
    if (guestScheduleDate === tomorrowISO) return "Tomorrow's Schedule";
    const [y, m, d] = guestScheduleDate.split('-').map(Number);
    const label = new Date(y, m - 1, d).toLocaleDateString('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
    });
    return `Schedule — ${label}`;
  }, [guestScheduleDate, calendarDay, tomorrowISO]);

  const guestScheduleForDate = useMemo(() => {
    const guestId = selectedGuest?.id || selectedGuest?.guestId || (externalMode ? lockedGuestId : null);
    const guestName = selectedGuest?.name || lockedGuestName;
    const query = guestQuery.trim().toLowerCase();
    if (!guestId && !guestName && !query) return [];

    const list = bookingsByDate[guestScheduleDate] || [];
    return list
      .filter((b) => {
        const bookingGuestId = b.guestId || b.guest_id;
        const bookingName = String(b.guestName || b.guest_name || '').toLowerCase();
        if (guestId && bookingGuestId != null && String(bookingGuestId) === String(guestId)) return true;
        if (guestName) {
          const gn = guestName.toLowerCase();
          if (bookingName === gn || bookingName.includes(gn) || gn.includes(bookingName)) return true;
        }
        if (query && (bookingName.includes(query) || String(bookingGuestId || '').toLowerCase().includes(query))) {
          return true;
        }
        return false;
      })
      .sort((a, b) => {
        const timeA = a.start_time || a.startTime || '';
        const timeB = b.start_time || b.startTime || '';
        return timeA.localeCompare(timeB);
      });
  }, [bookingsByDate, guestScheduleDate, selectedGuest, guestQuery, externalMode, lockedGuestId, lockedGuestName]);

  const bookingGuests = useMemo(() => {
    const byKey = new Map();
    for (const d of dateRange) {
      for (const b of bookingsByDate[d] || []) {
        const id = b.guestId || b.guest_id;
        const name = b.guestName || b.guest_name;
        if (!name && !id) continue;
        const key = String(id || name);
        if (!byKey.has(key)) {
          byKey.set(key, { id: id || name, name: name || String(id), fromBooking: true });
        }
      }
    }
    return Array.from(byKey.values());
  }, [bookingsByDate, dateRange]);

  const mergedAllGuests = useMemo(() => {
    const map = new Map();
    allGuests.forEach((g) => map.set(String(g.id || g.name), g));
    bookingGuests.forEach((g) => {
      const key = String(g.id);
      if (!map.has(key)) map.set(key, g);
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  }, [allGuests, bookingGuests]);

  const guestAutocompleteOptions = useMemo(() => {
    const q = guestQuery.trim().toLowerCase();
    const tabPool = guestFilter === 'upcoming'
      ? mergedAllGuests.filter((g) => isUpcoming(g, calendarDay))
      : guestFilter === 'inhouse'
        ? mergedAllGuests.filter((g) => isInHouse(g, calendarDay))
        : mergedAllGuests;
    if (!q) return tabPool;
    return mergedAllGuests.filter((g) => {
      const name = (g.name || '').toLowerCase();
      const id = String(g.id || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [guestQuery, guestFilter, mergedAllGuests, calendarDay]);

  const monthDays = useMemo(() => {
    const start = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
    const end = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0);
    const startDay = new Date(start);
    startDay.setDate(start.getDate() - start.getDay());
    const endDay = new Date(end);
    endDay.setDate(end.getDate() + (6 - end.getDay()));
    const days = [];
    for (let d = new Date(startDay); d <= endDay; d.setDate(d.getDate() + 1)) {
      days.push(formatDateISO(new Date(d)));
    }
    return days;
  }, [monthAnchor]);

  const fetchMonth = useCallback(async () => {
    // Fetch data for the timeline date range
    if (!dateRange.length) return;
    try {
      setLoading(true);
      const entries = await Promise.all(
        dateRange.map(async (d) => {
          const res = await apiGet(`/api/bookings/${d}`);
          const data = await res.json();
          return [d, Array.isArray(data) ? data : []];
        })
      );
      const map = {};
      for (const [d, list] of entries) map[d] = list;
      setBookingsByDate(map);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const fetchAvailableActivitiesForRange = useCallback(async () => {
    // In external mode, use lockedGuestId; otherwise require selectedGuest
    const hasGuest = selectedGuest || (externalMode && lockedGuestId);
    if (!dateRange.length || !hasGuest) {
      setAvailableActivitiesByDate({});
      return;
    }
    
    try {
      const startDate = dateRange[0];
      const endDate = dateRange[dateRange.length - 1];
      const response = await apiGet(`/api/activities/timetable/available?start_date=${startDate}&end_date=${endDate}`);
      const data = await response.json();
      setAvailableActivitiesByDate(data);
    } catch (error) {
      console.error('Error fetching available activities:', error);
      setAvailableActivitiesByDate({});
    }
  }, [dateRange, selectedGuest, externalMode, lockedGuestId]);

  useEffect(() => { fetchMonth(); }, [fetchMonth]);
  useEffect(() => { fetchAvailableActivitiesForRange(); }, [fetchAvailableActivitiesForRange]);

  const filterFn = useCallback((b) => {
    const gq = guestQuery.trim().toLowerCase();
    const rq = roomQuery.trim().toLowerCase();
    const guestName = String(b.guestName || b.guest_name || b.name || '').toLowerCase();
    const guestId = String(b.guestId || b.guest_id || b.id || '').toLowerCase();
    const roomName = String(b.room || b.room_name || '').toLowerCase();
    const therapistName = String(b.therapist || b.therapist_name || '').toLowerCase();
    const matchesGuest = !gq || guestName.includes(gq) || guestId.includes(gq);
    const matchesRoom = !rq || roomName.includes(rq);
    const matchesSelectedService = !selectedService || String(b.treatment || b.service || '').toLowerCase() === String(selectedService.name || selectedService).toLowerCase();
    const matchesSelectedTherapist = !selectedTherapist || therapistName === String(selectedTherapist.name || selectedTherapist).toLowerCase();
    return matchesGuest && matchesRoom && matchesSelectedService && matchesSelectedTherapist;
  }, [guestQuery, roomQuery, selectedService, selectedTherapist]);

  // Extract unique filter options from all guests
  const filterOptions = useMemo(() => {
    const extractUnique = (field) => {
      const values = allGuests
        .map(g => String(g[field] || '').trim())
        .filter(v => v);
      return [...new Set(values)].sort();
    };

    const ages = allGuests
      .map(g => getAgeRange(g.age || g.ageYears))
      .filter(v => v);
    const uniqueAges = [...new Set(ages)];
    const ageOrder = ['< 20', '20-30', '30-40', '40-50', '50-60', '60+'];
    const sortedAges = uniqueAges.sort((a, b) => ageOrder.indexOf(a) - ageOrder.indexOf(b));

    return {
      gender: extractUnique('gender'),
      guestTag: extractUnique('tag').concat(extractUnique('guestTag')),
      contactType: extractUnique('contactType'),
      package: extractUnique('program').concat(extractUnique('package')),
      ageRange: sortedAges,
      diet: extractUnique('diet').concat(extractUnique('dietType')),
      duration: extractUnique('nights'),
      consultingDoctor: extractUnique('consultingDoctor')
    };
  }, [allGuests]);

  // Count guests matching each filter option
  const getFilterCounts = useCallback((filterKey, option) => {
    return allGuests.filter(g => {
      if (filterKey === 'ageRange') {
        return getAgeRange(g.age || g.ageYears) === option;
      } else if (filterKey === 'package') {
        return String(g.program || g.package || '').trim() === option;
      } else if (filterKey === 'guestTag') {
        return String(g.tag || g.guestTag || '').trim() === option;
      } else if (filterKey === 'diet') {
        return String(g.diet || g.dietType || '').trim() === option;
      } else if (filterKey === 'duration') {
        return String(g.nights || '').trim() === option;
      }
      return String(g[filterKey] || '').trim() === option;
    }).length;
  }, [allGuests]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return Object.values(advancedFilters).reduce((sum, arr) => sum + arr.length, 0);
  }, [advancedFilters]);

  // Clear all advanced filters
  const clearAllFilters = () => {
    setAdvancedFilters({
      gender: [],
      guestTag: [],
      contactType: [],
      package: [],
      ageRange: [],
      diet: [],
      duration: [],
      consultingDoctor: []
    });
  };

  const filteredByDate = useMemo(() => {
    const out = {};
    for (const d of timelineDays) {
      const list = bookingsByDate[d] || [];
      let filtered = list.filter(filterFn);
      
      // In external mode, also filter by the locked guest
      if (externalMode && lockedGuestId) {
        filtered = filtered.filter(b => {
          const bookingGuestId = b.guestId || b.guest_id;
          return bookingGuestId === lockedGuestId;
        });
      }
      
      out[d] = filtered;
    }
    return out;
  }, [bookingsByDate, timelineDays, filterFn, externalMode, lockedGuestId]);

  const totalMatches = useMemo(() => Object.values(filteredByDate).reduce((acc, arr) => acc + (arr?.length || 0), 0), [filteredByDate]);

  const calendarGuestId = selectedGuest?.id || selectedGuest?.guestId || (externalMode ? lockedGuestId : null);

  const calendarGuestPriorityBookings = useMemo(() => {
    if (!calendarGuestId) return [];
    return Object.values(bookingsByDate).flat().filter((b) => {
      const bookingGuestId = b.guestId || b.guest_id;
      return bookingGuestId != null && String(bookingGuestId) === String(calendarGuestId);
    });
  }, [bookingsByDate, calendarGuestId]);

  const calendarBookingsForDate = useMemo(() => {
    if (!calendarGuestId || !date) return null;
    return (bookingsByDate[date] || []).filter((b) =>
      guestIdsMatch(b.guestId || b.guest_id, calendarGuestId)
    );
  }, [bookingsByDate, date, calendarGuestId]);

  // Keep timeline schedule grid aligned with the itinerary date
  useEffect(() => {
    if (activeView === 'timeline' && guestScheduleDate && date !== guestScheduleDate) {
      setDate(guestScheduleDate);
    }
  }, [activeView, guestScheduleDate]);

  const goPrevMonth = () => { setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1)); };
  const goNextMonth = () => { setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1)); };
  const monthLabel = useMemo(() => monthAnchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }), [monthAnchor]);
  const dateRangeLabel = useMemo(() => formatDisplayDateRange(timelineDays), [timelineDays]);
  const listHeader = useMemo(() => {
    const g = guestQuery.trim();
    if (g) return `Results — ${g}`;
    return 'Results';
  }, [guestQuery]);

  // Timeline popover
  const [timelineAnchor, setTimelineAnchor] = useState(null);
  const [timelineBooking, setTimelineBooking] = useState(null);
  const openTimelineBooking = (booking, event) => { setTimelineBooking(booking); setTimelineAnchor(event?.currentTarget || null); };
  const closeTimelineBooking = () => { setTimelineAnchor(null); setTimelineBooking(null); };

  // Fetch available activities for the focused schedule date
  const fetchAvailableActivities = useCallback(async () => {
    try {
      setActivityLoading(true);
      const response = await apiGet(`/api/activities/timetable/available?date=${guestScheduleDate}`);
      const activities = await response.json();
      setAvailableActivities(activities);
    } catch (error) {
      console.error('Error fetching activities:', error);
      setAvailableActivities([]);
    } finally {
      setActivityLoading(false);
    }
  }, [guestScheduleDate]);

  // Enroll guest in activity
  const enrollInActivity = async (activity) => {
    if (!selectedGuest) return;
    
    try {
      const response = await apiPost('/api/schedule/confirm', {
        guestName: selectedGuest.name,
        guestId: selectedGuest.id,
        treatment: activity.activityName,
        date: activity.date,
        startTime: activity.startTime,
        endTime: activity.endTime,
        therapist: activity.instructorName,
        room: activity.roomName,
        activityTimetableId: activity.id,
        generalInstruction: '',
        treatmentInstruction: ''
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to enroll in activity');
      }
      
      alert(`✅ ${selectedGuest.name} enrolled in ${activity.activityName}!`);
      
      // Refresh data
      await fetchMonth();
      await fetchAvailableActivities();
      setShowActivityModal(false);
    } catch (error) {
      console.error('Error enrolling in activity:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  // Remove guest from activity
  const removeFromActivity = async (booking) => {
    if (!selectedGuest) return;
    
    const confirmed = window.confirm(
      `Remove ${selectedGuest.name} from ${booking.treatment}?`
    );
    
    if (!confirmed) return;
    
    try {
      const bookingId = booking.id || booking.bookingId;
      const bookingDate = booking.date || formatDateISO(new Date());
      
      const response = await apiDelete(`/api/bookings/${bookingDate}/${bookingId}`);
      
      if (!response.ok) {
        throw new Error('Failed to remove from activity');
      }
      
      alert(`✅ ${selectedGuest.name} removed from ${booking.treatment}`);
      
      // Refresh data
      await fetchMonth();
      await fetchAvailableActivities();
    } catch (error) {
      console.error('Error removing from activity:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  // When guest or focused date changes, refresh available activities
  useEffect(() => {
    if (selectedGuest) {
      fetchAvailableActivities();
    } else {
      setAvailableActivities([]);
    }
  }, [selectedGuest, guestScheduleDate, fetchAvailableActivities]);

  // Timeline view helpers
  const SLOT_MINUTES = timelineViewMode === 'compact' ? 30 : 15;
  const SLOT_HEIGHT = timelineViewMode === 'compact' ? 28 : 40;

  const colorForTreatment = (name) => {
    const str = String(name || '');
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    const hue = Math.abs(hash) % 360;
    const bg = `hsl(${hue}, 80%, ${timelineViewMode === 'compact' ? 93 : 92}%)`;
    const bd = `hsl(${hue}, 60%, ${timelineViewMode === 'compact' ? 65 : 62}%)`;
    return { bg, bd };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, gap: 2, bgcolor: '#f6f6f6' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <GoldButtonGroup variant="outlined" size="small">
          <ViewButton selected={activeView === 'desk'} onClick={() => setActiveView('desk')} startIcon={<LocalHospitalOutlinedIcon fontSize="small" />}>Desk</ViewButton>
          <ViewButton selected={activeView === 'timeline'} onClick={() => setActiveView('timeline')} startIcon={<CalendarMonthOutlinedIcon fontSize="small" />}>Timeline</ViewButton>
        </GoldButtonGroup>
      </Box>

      {activeView === 'desk' && (
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 2, overflowX: 'auto', pb: 1 }}>
          {/* Left: Filters Panel */}
          <Paper sx={{ 
            width: sidePanelsCollapsed ? 48 : 360, 
            minWidth: sidePanelsCollapsed ? 48 : 360,
            flexShrink: 0,
            p: sidePanelsCollapsed ? 1 : 2, 
            overflow: 'auto', 
            bgcolor: '#fff', 
            border: '1px solid #e5e7eb', 
            borderRadius: 2, 
            boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px',
            transition: 'width 0.3s ease, min-width 0.3s ease, padding 0.3s ease',
            position: 'relative'
          }}>
            {/* Collapse/Expand button - controls both panels */}
            <IconButton
              size="small"
              onClick={() => setSidePanelsCollapsed(!sidePanelsCollapsed)}
              sx={{
                position: 'absolute',
                right: 4,
                top: 4,
                zIndex: 10,
                bgcolor: '#E4C984',
                color: '#171717',
                width: 28,
                height: 28,
                '&:hover': { bgcolor: '#d4b974' }
              }}
              title={sidePanelsCollapsed ? "Expand panels" : "Collapse panels"}
            >
              {sidePanelsCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
            
            {sidePanelsCollapsed ? (
              <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <LocalHospitalOutlinedIcon sx={{ color: '#E4C984' }} />
                <ListAltIcon sx={{ color: '#E4C984', mt: 1 }} />
              </Box>
            ) : (
              <>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Doctor Desk</Typography>
            
            {/* External Mode: Show locked guest (read-only) */}
            {externalMode ? (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e5e7eb' }}>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                  Guest (Locked)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#171717' }}>
                  {lockedGuestName || selectedGuest?.name || 'Loading...'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  {lockedGuestId || selectedGuest?.id || ''}
                  {selectedGuest?.room ? ` • Room ${selectedGuest.room}` : ''}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Guest Filter Toggle */}
                <ButtonGroup 
                  fullWidth 
                  size="small" 
                  sx={{ 
                    mb: 2,
                    '& .MuiButton-root': {
                      borderColor: '#E4C984',
                      color: '#666',
                      '&:hover': {
                        borderColor: '#E4C984',
                        backgroundColor: 'rgba(228, 201, 132, 0.1)',
                      }
                    },
                    '& .MuiButton-root.Mui-selected': {
                      backgroundColor: '#E4C984',
                      color: '#fff',
                      borderColor: '#E4C984',
                      '&:hover': {
                        backgroundColor: '#d4b874',
                      }
                    }
                  }}
                >
                  <Button
                    className={guestFilter === 'upcoming' ? 'Mui-selected' : ''}
                    onClick={() => setGuestFilter('upcoming')}
                  >
                    Upcoming ({apiCounts.upcoming})
                  </Button>
                  <Button
                    className={guestFilter === 'inhouse' ? 'Mui-selected' : ''}
                    onClick={() => setGuestFilter('inhouse')}
                  >
                    In-house ({apiCounts.inHouse})
                  </Button>
                </ButtonGroup>

                {/* Guest Search */}
                <Autocomplete
                  options={guestAutocompleteOptions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.name || option.id || '';
                  }}
                  value={mergedAllGuests.find(g => g.name === guestQuery || g.id === guestQuery) || null}
                  onChange={(e, newValue) => {
                    if (newValue) {
                      setGuestQuery(newValue.name || newValue.id || '');
                      setSelectedGuest(newValue);
                    } else {
                      setGuestQuery('');
                      setSelectedGuest(null);
                    }
                  }}
                  onInputChange={(e, newInputValue) => {
                    if (e && e.type === 'change') {
                      setGuestQuery(newInputValue);
                      const matchingGuest = mergedAllGuests.find((g) => {
                        const name = (g.name || '').toLowerCase();
                        const id = String(g.id || '').toLowerCase();
                        const input = newInputValue.toLowerCase();
                        return name === input || id === input || name.includes(input);
                      });
                      setSelectedGuest(matchingGuest || null);
                    }
                  }}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Guest name or ID"
                      placeholder="e.g. John or GU001"
                    />
                  )}
                  renderOption={(props, option) => {
                    const dateRange = formatDateRange(option);
                    return (
                      <li {...props} key={option.id || option.name}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {option.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {option.id}{option.room ? ` • Room ${option.room}` : ''}{dateRange ? ` • ${dateRange}` : ''}
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                  sx={{ mb: 1.5 }}
                />
              </>
            )}
            
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Autocomplete
              options={(config.treatments || []).map(t => t.name).sort((a, b) => a.localeCompare(b))}
              value={selectedService ? (selectedService.name || selectedService) : null}
              onChange={(e, val) => setSelectedService(val)}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Service (treatment)" placeholder="Select service" />
              )}
              sx={{ mb: 1.5 }}
              clearOnEscape
            />
            <Autocomplete
              options={Object.values(config.therapists || {}).map(t => t.name).sort((a, b) => a.localeCompare(b))}
              value={selectedTherapist ? (selectedTherapist.name || selectedTherapist) : null}
              onChange={(e, val) => setSelectedTherapist(val)}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Therapist (optional)" placeholder="Select therapist" />
              )}
              sx={{ mb: 1.5 }}
              clearOnEscape
            />
            
              <TextField
                fullWidth
                size="small"
                label="Room (optional)"
                placeholder="Room name/number"
                value={roomQuery}
                onChange={(e) => setRoomQuery(e.target.value)}
              sx={{ mb: 2 }}
              />

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  await fetchMonth();
                  const first = dateRange.find(d => (filteredByDate[d] || []).length > 0);
                  if (first) setDate(first);
                }}
                sx={{ bgcolor: '#E4C984', '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.8)' }, color: '#171717' }}
              >
                Search
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />
            
            {/* Today's Schedule for Selected Guest - Also show in external mode with locked guest */}
            {(selectedGuest || guestQuery.trim() || (externalMode && lockedGuestId)) && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {guestScheduleTitle} - {selectedGuest?.name || guestQuery.trim() || lockedGuestName || lockedGuestId}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ListAltIcon />}
                    onClick={() => setShowItinerary(true)}
                    sx={{
                      borderColor: '#E4C984',
                      color: '#171717',
                      textTransform: 'none',
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      '&:hover': {
                        borderColor: '#cbb16f',
                        backgroundColor: 'rgba(228, 201, 132, 0.08)'
                      }
                    }}
                  >
                    View Itinerary
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {(() => {
                    const [y, m, d] = guestScheduleDate.split('-').map(Number);
                    return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
                      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
                    });
                  })()}
                </Typography>
                
                {guestScheduleForDate.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    No appointments on this day
                  </Typography>
                ) : (
                  <List dense sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 1, p: 0 }}>
                    {guestScheduleForDate.map((booking, idx) => {
                      const isActivity = booking.activity_timetable_id || booking.activityTimetableId;
                      const startTime = booking.start_time || booking.startTime;
                      const treatment = booking.treatment;
                      const room = booking.room;
                      
                      return (
                        <ListItem 
                          key={idx}
                          sx={{ 
                            py: 0.5,
                            pr: 6,
                            bgcolor: isActivity ? '#fef3c7' : 'transparent',
                            '&:hover': { bgcolor: isActivity ? '#fef08a' : 'rgba(0, 0, 0, 0.04)' },
                            cursor: 'pointer',
                            borderLeft: isActivity ? '3px solid #fbbf24' : '3px solid transparent'
                          }}
                          onClick={() => { 
                            setDate(guestScheduleDate); 
                            setSelectedBooking(enrichBookingForEdit(booking, guestScheduleDate)); 
                            setShowEditModal(true); 
                          }}
                          secondaryAction={
                            isActivity ? (
                              <IconButton 
                                edge="end" 
                                size="small"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  removeFromActivity(booking);
                                }}
                                sx={{ 
                                  color: '#dc2626',
                                  '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.1)' }
                                }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <IconButton 
                                edge="end" 
                                size="small"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setDate(guestScheduleDate);
                                  setSelectedBooking(enrichBookingForEdit(booking, guestScheduleDate)); 
                                  setShowEditModal(true); 
                                }}
                                sx={{ 
                                  color: '#E4C984',
                                  '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.2)' }
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            )
                          }
                        >
                          <ListItemText
                            primary={`${startTime} ${treatment}${isActivity ? ' 🧘' : ''}`}
                            secondary={(
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                  {selectedGuest?.name || lockedGuestName || ''} • Room {room}
                                </Typography>
                                {renderBookingTherapistChip(booking)}
                              </Box>
                            )}
                            primaryTypographyProps={{ fontSize: '0.85rem' }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
                
                {/* Add Activity Button - Hidden in external mode */}
                {!externalMode && (
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => setShowActivityModal(true)}
                    sx={{
                      mt: 1.5,
                      borderColor: '#E4C984',
                      color: '#171717',
                      '&:hover': {
                        borderColor: '#cbb16f',
                        backgroundColor: 'rgba(228, 201, 132, 0.08)'
                      }
                    }}
                  >
                    + Add Activity for {guestScheduleDate === calendarDay ? 'Today' : formatDisplayDateRange([guestScheduleDate])}
                  </Button>
                )}
                
                {/* Clear All Schedule Button - Hidden in external mode */}
                {!externalMode && (
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={async () => {
                      const guestName = selectedGuest?.name || guestQuery;
                      const guestId = selectedGuest?.id || guestQuery;
                      
                      const confirmed = window.confirm(
                        `Clear ALL future bookings for ${guestName}?\n\n` +
                        `⚠️ This will:\n` +
                        `• Delete all future appointments\n` +
                        `• Remove all associated billing entries\n` +
                        `• Cannot be undone\n\n` +
                        `Past bookings will be preserved.\n\n` +
                        `Continue?`
                      );
                      
                      if (!confirmed) return;
                      
                      const reason = window.prompt('Please provide a reason for clearing the schedule:');
                      if (!reason) return;
                      
                      try {
                        const response = await apiPost('/api/guest-cleanup/clear-guest-bookings', {
                            guest_id: guestId,
                            guest_name: guestName,
                            reason: reason
                          });
                        
                        if (!response.ok) {
                          const errorData = await response.json();
                          throw new Error(errorData.detail || 'Failed to clear bookings');
                        }
                        
                        const result = await response.json();
                        alert(
                          `✅ Schedule cleared successfully!\n\n` +
                          `Future bookings deleted: ${result.bookings_deleted}\n\n` +
                          `${result.message}\n\n` +
                          `Note: Past bookings and billing records are preserved.`
                        );
                        
                        // Refresh the calendar
                        await fetchMonth();
                      } catch (error) {
                        console.error('Error clearing schedule:', error);
                        alert(`❌ Error clearing schedule: ${error.message}`);
                      }
                    }}
                    sx={{
                      mt: 1,
                      borderColor: '#dc2626',
                      color: '#dc2626',
                      '&:hover': {
                        borderColor: '#b91c1c',
                        backgroundColor: 'rgba(220, 38, 38, 0.04)'
                      }
                    }}
                  >
                    🗑️ Clear All Schedule for {selectedGuest.name}
                  </Button>
                )}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {loading && <Chip label="Loading" size="small" />}
              <Chip label={`${totalMatches} results`} size="small" />
            </Box>
              </>
            )}
          </Paper>

          {/* Middle: Results list grouped by day - hidden when collapsed */}
          {!sidePanelsCollapsed && (
          <Paper sx={{ 
            width: 420, 
            minWidth: 420,
            flexShrink: 0,
            p: 2, 
            overflow: 'auto', 
            bgcolor: '#fff', 
            border: '1px solid #e5e7eb', 
            borderRadius: 2, 
            boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px'
          }}>
            {/* Day Filter Tabs */}
            <Box sx={{ mb: 2 }}>
              <GoldButtonGroup variant="outlined" size="small" fullWidth>
                <ViewButton 
                  selected={dayFilter === 'today'} 
                  onClick={() => {
                    setDayFilter('today');
                    setDate(todayISO);
                  }}
                >
                  Today
                </ViewButton>
                <ViewButton 
                  selected={dayFilter === 'tomorrow'} 
                  onClick={() => {
                    setDayFilter('tomorrow');
                    setDate(tomorrowISO);
                  }}
                >
                  Tomorrow
                </ViewButton>
                <ViewButton 
                  selected={dayFilter === 'next7days'} 
                  onClick={() => setDayFilter('next7days')}
                >
                  7 Days
                </ViewButton>
                <ViewButton 
                  selected={dayFilter === 'all'} 
                  onClick={() => setDayFilter('all')}
                >
                  All
                </ViewButton>
              </GoldButtonGroup>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{listHeader}</Typography>
              <Typography variant="body2" color="text.secondary">{dateRangeLabel}</Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <List dense>
              {Object.keys(filteredByDate).sort().map((d) => {
                const rows = filteredByDate[d] || [];
                if (rows.length === 0) return null;
                // Sort rows by startTime chronologically
                const sortedRows = [...rows].sort((a, b) => {
                  const aTime = a.startTime || a.start_time || '00:00';
                  const bTime = b.startTime || b.start_time || '00:00';
                  return aTime.localeCompare(bTime);
                });
                return (
                  <Box key={d} sx={{ mb: 1.25 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {(() => {
                        const [y, m, day] = d.split('-').map(Number);
                        return new Date(y, m - 1, day).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
                      })()}
                    </Typography>
                    {sortedRows.map((b, idx) => (
                      <ListItem 
                        key={b.bookingId || `${d}-${b.startTime}-${idx}`} 
                        sx={{ 
                          py: 0.5, 
                          pr: 6,
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          cursor: 'pointer'
                        }}
                        onClick={() => { setDate(d); setSelectedBooking(enrichBookingForEdit(b, d)); setShowEditModal(true); }}
                        secondaryAction={
                          <IconButton 
                            edge="end" 
                            size="small"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setDate(d); 
                              setSelectedBooking(enrichBookingForEdit(b, d)); 
                              setShowEditModal(true); 
                            }}
                            sx={{ 
                              color: '#E4C984',
                              '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.2)' }
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={`${b.startTime || b.start_time} ${b.treatment}`}
                          secondary={(
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                {b.guestName || b.guest_name} • Room {b.room}
                              </Typography>
                              {renderBookingTherapistChip(b)}
                            </Box>
                          )}
                          primaryTypographyProps={{ fontSize: '0.85rem' }}
                        />
                      </ListItem>
                    ))}
                  </Box>
                );
              })}
              {totalMatches === 0 && (
                <Typography variant="caption" color="text.disabled">No matching bookings this month</Typography>
              )}
            </List>
          </Paper>
          )}

          {/* Right: Timeline view */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ height: '100%', p: 2, overflow: 'hidden', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Timeline
                  {guestQuery && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                      {guestQuery}
                    </Typography>
                  )}
                </Typography>
                <ButtonGroup size="small" variant="outlined">
                  <Button 
                    onClick={() => setTimelineViewMode('normal')} 
                    variant={timelineViewMode === 'normal' ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: timelineViewMode === 'normal' ? '#E4C984' : 'transparent',
                      color: timelineViewMode === 'normal' ? '#171717' : '#666',
                      borderColor: '#E4C984',
                      '&:hover': {
                        backgroundColor: timelineViewMode === 'normal' ? '#d4b974' : 'rgba(228, 201, 132, 0.08)',
                        borderColor: '#E4C984'
                      }
                    }}
                  >
                    Normal
                  </Button>
                  <Button 
                    onClick={() => setTimelineViewMode('compact')} 
                    variant={timelineViewMode === 'compact' ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: timelineViewMode === 'compact' ? '#E4C984' : 'transparent',
                      color: timelineViewMode === 'compact' ? '#171717' : '#666',
                      borderColor: '#E4C984',
                      '&:hover': {
                        backgroundColor: timelineViewMode === 'compact' ? '#d4b974' : 'rgba(228, 201, 132, 0.08)',
                        borderColor: '#E4C984'
                      }
                    }}
                  >
                    Compact
                  </Button>
                </ButtonGroup>
              </Box>
              <Box sx={{ overflow: 'auto', position: 'relative', maxHeight: 'calc(100vh - 220px)' }}>
                <table className="w-full border-collapse relative">
                <thead>
                  <tr>
                      <th className="p-2 border sticky left-0 top-0 bg-white z-30 min-w-[60px]">Time</th>
                      {timelineDays.map((d) => (
                        <th key={d} className="p-2 border min-w-[180px] sticky top-0 bg-white z-20">
                          <div className="font-semibold">{new Date(d).toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                          <div className="text-xs text-gray-500">{new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</div>
                        </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                    {Array.from({ length: Math.floor(((19 - 6) * 60) / SLOT_MINUTES) + 1 }).map((_, idx) => {
                      const minutes = 6 * 60 + idx * SLOT_MINUTES;
                      const h = Math.floor(minutes / 60);
                      const m = minutes % 60;
                      const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                        return (
                        <tr key={label} style={{ height: `${SLOT_HEIGHT}px` }}>
                          <td className="p-1 border text-sm sticky left-0 bg-white z-20">{label}</td>
                          {timelineDays.map((d) => {
                            const dayBookings = filteredByDate[d] || [];
                            const bookingsAtSlot = dayBookings
                              .filter(b => b.startTime || b.start_time)
                              .filter(b => {
                                const startTime = b.startTime || b.start_time;
                                const [bh, bm] = startTime.split(':').map(Number);
                                const bMin = bh * 60 + bm;
                                return Math.floor(bMin / SLOT_MINUTES) * SLOT_MINUTES === minutes;
                              });
                            
                            // Check for available activities at this slot
                            const availableActivitiesForDay = availableActivitiesByDate[d] || [];
                            
                            // Get ALL bookings for this guest on this day (not just this slot) to check for overlaps
                            const allGuestBookingsForDay = dayBookings;
                            
                            // Filter activities at this slot that DON'T overlap with any guest booking
                            const activitiesAtSlot = availableActivitiesForDay.filter(activity => {
                              const [ah, am] = activity.startTime.split(':').map(Number);
                              const aMin = ah * 60 + am;
                              const isAtThisSlot = Math.floor(aMin / SLOT_MINUTES) * SLOT_MINUTES === minutes;
                              if (!isAtThisSlot) return false;
                              
                              // Check if activity overlaps with any of the guest's bookings
                              const [actStartH, actStartM] = activity.startTime.split(':').map(Number);
                              const [actEndH, actEndM] = activity.endTime.split(':').map(Number);
                              const actStart = actStartH * 60 + actStartM;
                              const actEnd = actEndH * 60 + actEndM;
                              
                              const overlapsWithBooking = allGuestBookingsForDay.some(booking => {
                                const bStart = booking.startTime || booking.start_time;
                                const bEnd = booking.endTime || booking.end_time;
                                if (!bStart || !bEnd) return false;
                                const [bsh, bsm] = bStart.split(':').map(Number);
                                const [beh, bem] = bEnd.split(':').map(Number);
                                const bookingStart = bsh * 60 + bsm;
                                const bookingEnd = beh * 60 + bem;
                                // Activity overlaps if it starts before booking ends AND ends after booking starts
                                return actStart < bookingEnd && actEnd > bookingStart;
                              });
                              
                              return !overlapsWithBooking;
                            });
                            
                            // Check if date is today or in the future
                            const slotDate = new Date(d);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            slotDate.setHours(0, 0, 0, 0);
                            const isTodayOrFuture = slotDate >= today;
                            
                            const hasBookings = bookingsAtSlot.length > 0;
                            const hasGuestContext = selectedGuest || (externalMode && lockedGuestId);
                            const hasAvailableActivities = activitiesAtSlot.length > 0 && hasGuestContext && isTodayOrFuture;
                            
                            return (
                              <td key={`${d}-${label}`} className="relative border p-0">
                                {bookingsAtSlot.map((b) => {
                                    const startTime = b.startTime || b.start_time;
                                    const endTime = b.endTime || b.end_time;
                                    if (!startTime || !endTime) return null;
                                    const [sh, sm] = startTime.split(':').map(Number);
                                    const [eh, em] = endTime.split(':').map(Number);
                                    const dur = eh * 60 + em - (sh * 60 + sm);
                                    const rows = Math.max(1, dur / SLOT_MINUTES);
                                    const colors = colorForTreatment(b.treatment);
                                    const isActivity = b.activity_timetable_id || b.activityTimetableId;
                                    return (
                                      <div 
                                        key={b.bookingId || b.id || `${b.guestName}-${startTime}-${b.room}`}
                                        className="absolute left-0 right-0 px-1 py-0.5 text-[11px] overflow-hidden cursor-pointer"
                                        style={{ 
                                          height: `${rows * SLOT_HEIGHT}px`, 
                                          top: 0, 
                                          backgroundColor: colors.bg, 
                                          border: `1px solid ${colors.bd}`,
                                          borderLeft: isActivity ? '3px solid #fbbf24' : `1px solid ${colors.bd}`
                                        }}
                                        onClick={() => { 
                                          setDate(d); 
                                          setSelectedBooking(enrichBookingForEdit(b, d)); 
                                          setShowEditModal(true); 
                                        }}
                                      >
                                        <div className="truncate font-semibold">{b.treatment}{isActivity ? ' 🧘' : ''}</div>
                                        {timelineViewMode !== 'compact' && (
                                          <>
                                            <div className="text-[10px] text-gray-600 truncate">{b.guestName || b.guest_name}</div>
                                            <div className="text-[10px] text-gray-600 truncate" title={getTherapistDisplayTitle(b)}>
                                              {b.room} • {formatCompactTherapistDisplay(b)}
                                            </div>
                                            {(b.therapistsCount > 1 || (b.therapists?.length || 0) > 1) && (
                                              <div className="text-[9px] text-blue-600">👥 {b.therapistsCount || b.therapists.length}</div>
                                            )}
                                          </>
                                        )}
                            </div>
                                    );
                                  })}
                                
                                {/* Show available activities in empty slots */}
                                {!hasBookings && hasAvailableActivities && (
                                  <div className="absolute inset-0 flex gap-0.5 m-0.5">
                                    {activitiesAtSlot.map((activity, actIdx) => {
                                      // Calculate duration and height for each activity
                                      const [ash, asm] = activity.startTime.split(':').map(Number);
                                      const [aeh, aem] = activity.endTime.split(':').map(Number);
                                      const actDur = aeh * 60 + aem - (ash * 60 + asm);
                                      const actRows = Math.max(1, actDur / SLOT_MINUTES);
                                      const actHeight = actRows * SLOT_HEIGHT;
                                      // Divide width among activities
                                      const widthPercent = 100 / activitiesAtSlot.length;
                                      
                                      return (
                                        <div
                                          key={activity.id || actIdx}
                                          className="px-1 py-0.5 text-[10px] overflow-hidden cursor-pointer hover:opacity-90"
                                          style={{
                                            height: `${actHeight}px`,
                                            width: `${widthPercent}%`,
                                            minWidth: activitiesAtSlot.length <= 2 ? '60px' : '40px',
                                            backgroundColor: '#fef3c7',
                                            border: '2px dashed #f59e0b',
                                            borderRadius: '3px'
                                          }}
                                          onClick={() => {
                                            setSelectedSlotInfo({
                                              date: d,
                                              time: label,
                                              activities: activitiesAtSlot
                                            });
                                            setShowAvailableActivitiesModal(true);
                                          }}
                                          title={`${activity.activityName} - ${activity.startTime}-${activity.endTime} (${activity.currentEnrollment}/${activity.maxCapacity})`}
                                        >
                                          <div className="font-semibold truncate text-amber-700" style={{ fontSize: activitiesAtSlot.length > 2 ? '9px' : '10px' }}>
                                            📅 {activity.activityName}
                                          </div>
                                          {timelineViewMode !== 'compact' && activitiesAtSlot.length <= 2 && (
                                            <>
                                              <div className="text-[9px] text-amber-600 truncate">
                                                {activity.startTime}-{activity.endTime}
                                              </div>
                                              <div className="text-[9px] text-amber-600 truncate">
                                                👥 {activity.currentEnrollment}/{activity.maxCapacity}
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                          </td>
                        );
                      })}
                    </tr>
                      );
                    })}
                </tbody>
              </table>
              </Box>
            </Paper>
          </Box>
        </Box>
      )}

      {activeView === 'timeline' && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Selected Guest Itinerary Header */}
          {(selectedGuest || (externalMode && lockedGuestId)) && (
            <Paper sx={{ p: 2, bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Itinerary - {selectedGuest?.name || lockedGuestName || lockedGuestId}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ListAltIcon />}
                  onClick={() => setShowItinerary(true)}
                  sx={{
                    borderColor: '#E4C984',
                    color: '#171717',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#cbb16f',
                      backgroundColor: 'rgba(228, 201, 132, 0.08)'
                    }
                  }}
                >
                  View Full Itinerary
                </Button>
              </Box>
              {guestScheduleForDate.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No appointments scheduled for this day
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {guestScheduleForDate.slice(0, 5).map((booking, idx) => {
                    const isActivity = booking.activity_timetable_id || booking.activityTimetableId;
                    const startTime = booking.start_time || booking.startTime;
                    return (
                      <Chip
                        key={idx}
                        label={`${startTime} ${booking.treatment}${isActivity ? ' 🧘' : ''}`}
                        size="small"
                        sx={{
                          bgcolor: isActivity ? '#fef3c7' : '#f3f4f6',
                          borderLeft: isActivity ? '3px solid #fbbf24' : '3px solid #E4C984',
                          '& .MuiChip-label': { fontSize: '0.75rem' }
                        }}
                      />
                    );
                  })}
                  {guestScheduleForDate.length > 5 && (
                    <Chip
                      label={`+${guestScheduleForDate.length - 5} more`}
                      size="small"
                      sx={{ bgcolor: '#f3f4f6' }}
                    />
                  )}
                </Box>
              )}
            </Paper>
          )}
          <Paper sx={{ flex: 1, p: 2, overflow: 'hidden', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Schedule: {(selectedGuest?.name || lockedGuestName || lockedGuestId) || 'All Guests'}
              </Typography>
            </Box>
            <CalendarPanel
              selectedDate={date}
              onDateChange={setDate}
              dateRange={dateRange}
              bookings={calendarBookingsForDate}
              config={config}
              onEditBooking={(booking) => { setSelectedBooking(enrichBookingForEdit(booking, booking.date)); setShowEditModal(true); }}
              headerVariant="compact"
              filterGuestId={calendarGuestId}
              guestPriorityBookings={calendarGuestPriorityBookings}
            />
          </Paper>
        </Box>
      )}

      {showEditModal && selectedBooking && (
        <EditBookingModal
          show={showEditModal}
          booking={selectedBooking}
          guestContext={selectedGuest}
          config={config}
          onClose={() => { setShowEditModal(false); setSelectedBooking(null); }}
          onDelete={async (b) => {
            try {
              const id = b.id || b.bookingId;
              const bookingDate = b.date;
              if (!id || !bookingDate) return;
              const resp = await apiDelete(`/api/bookings/${bookingDate}/${id}`);
              if (!resp.ok) {
                try { const j = await resp.json(); console.error(j.detail || 'Delete failed'); } catch {}
              }
              await fetchMonth();
              setShowEditModal(false);
              setSelectedBooking(null);
            } catch {}
          }}
          onSchedulingComplete={async () => { await fetchMonth(); setShowEditModal(false); setSelectedBooking(null); }}
        />
      )}

      {/* Activity Enrollment Modal */}
      {showActivityModal && selectedGuest && (
        <Dialog open={showActivityModal} onClose={() => setShowActivityModal(false)} fullWidth maxWidth="md">
          <DialogTitle>
            Add Activity for {selectedGuest.name} - {formatDisplayDateRange([guestScheduleDate])}
          </DialogTitle>
          <DialogContent dividers>
            {activityLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>Loading activities...</Typography>
              </Box>
            ) : availableActivities.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No activities scheduled for this day</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {availableActivities.map((activity) => {
                  // Check for conflicts
                  const hasConflict = guestScheduleForDate.some(booking => {
                    const bookingStart = booking.start_time || booking.startTime;
                    const bookingEnd = booking.end_time || booking.endTime;
                    return !(activity.endTime <= bookingStart || activity.startTime >= bookingEnd);
                  });
                  
                  const isFull = activity.currentEnrollment >= activity.maxCapacity;
                  const isUnavailable = hasConflict || isFull;
                  
                  return (
                    <Paper
                      key={activity.id}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: isUnavailable ? '#fca5a5' : '#d1fae5',
                        bgcolor: isUnavailable ? '#fef2f2' : '#f0fdf4',
                        opacity: isUnavailable ? 0.7 : 1
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
                            {isUnavailable ? '⚠️ ' : '✅ '}{activity.activityName}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '0.875rem' }}>
                            <Typography variant="body2">
                              ⏰ {activity.startTime} - {activity.endTime}
                            </Typography>
                            <Typography variant="body2">
                              👤 {activity.instructorName}
                            </Typography>
                            <Typography variant="body2">
                              🏠 {activity.roomName}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              👥 {activity.currentEnrollment}/{activity.maxCapacity} enrolled
                            </Typography>
                            
                            {hasConflict && (
                              <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 600 }}>
                                ⚠️ CONFLICT: Guest has overlapping appointment
                              </Typography>
                            )}
                            {isFull && !hasConflict && (
                              <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 600 }}>
                                🚫 FULL: Capacity reached
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        
                        <Button
                          variant="contained"
                          size="small"
                          disabled={isUnavailable}
                          onClick={() => enrollInActivity(activity)}
                          sx={{
                            bgcolor: isUnavailable ? '#9ca3af' : '#E4C984',
                            color: '#171717',
                            '&:hover': {
                              bgcolor: isUnavailable ? '#9ca3af' : '#d4b974'
                            },
                            '&:disabled': {
                              bgcolor: '#e5e7eb',
                              color: '#9ca3af'
                            }
                          }}
                        >
                          {isUnavailable ? 'UNAVAILABLE' : 'SELECT'}
                        </Button>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowActivityModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Available Activities Enrollment Modal */}
      {showAvailableActivitiesModal && selectedSlotInfo && (selectedGuest || (externalMode && lockedGuestId)) && (
        <Dialog 
          open={showAvailableActivitiesModal} 
          onClose={() => setShowAvailableActivitiesModal(false)} 
          fullWidth 
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 2,
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: '#E4C984',
              color: '#171717',
              p: 2.5,
              position: 'relative'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#171717' }}>
              Available Activities
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(23,23,23,0.75)' }}>
              {selectedGuest?.name || lockedGuestName || lockedGuestId} • {selectedSlotInfo.date} at {selectedSlotInfo.time}
            </Typography>
            <IconButton
              onClick={() => setShowAvailableActivitiesModal(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: '#171717'
              }}
            >
              <ClearIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedSlotInfo.activities.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No activities available at this time</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                {selectedSlotInfo.activities.map((activity) => {
                  // Calculate duration from start and end time if not provided
                  let defaultDuration = activity.duration;
                  if (!defaultDuration && activity.startTime && activity.endTime) {
                    const [sh, sm] = activity.startTime.split(':').map(Number);
                    const [eh, em] = activity.endTime.split(':').map(Number);
                    defaultDuration = (eh * 60 + em) - (sh * 60 + sm);
                  }
                  const activityDuration = activityDurations[activity.id] || defaultDuration || 60;
                  const capacityPercent = activity.maxCapacity > 0 
                    ? Math.round((activity.currentEnrollment / activity.maxCapacity) * 100) 
                    : 0;
                  
                  return (
                    <Paper
                      key={activity.id}
                      sx={{
                        p: 3,
                        border: '1px solid #e5e7eb',
                        bgcolor: '#fff',
                        borderRadius: 2,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      {/* Header with Activity Name and Enroll Button */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2.5 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
                          {activity.activityName}
                        </Typography>
                        <Button
                          variant="contained"
                          size="large"
                          onClick={async () => {
                            const guestName = selectedGuest?.name || lockedGuestName;
                            const guestId = selectedGuest?.id || lockedGuestId;
                            if (!guestId) return;
                            
                            try {
                              // Calculate end time based on custom duration
                              const [startHour, startMin] = activity.startTime.split(':').map(Number);
                              const startMinutes = startHour * 60 + startMin;
                              const endMinutes = startMinutes + activityDuration;
                              const endHour = Math.floor(endMinutes / 60);
                              const endMin = endMinutes % 60;
                              const endTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
                              
                              const response = await apiPost('/api/schedule/confirm', {
                                  guestName: guestName,
                                  guestId: guestId,
                                  treatment: activity.activityName,
                                  date: selectedSlotInfo.date,
                                  startTime: activity.startTime,
                                  endTime: endTime,
                                  therapist: activity.instructorName,
                                  room: activity.roomName,
                                  activityTimetableId: activity.id,
                                  generalInstruction: '',
                                  treatmentInstruction: `Booked for ${activityDuration} minutes`
                                });
                              
                              if (!response.ok) {
                                const errorData = await response.json();
                                throw new Error(errorData.detail || 'Failed to enroll in activity');
                              }
                              
                              alert(`✅ ${guestName || guestId} enrolled in ${activity.activityName} for ${activityDuration} minutes!`);
                              
                              // Refresh data
                              await fetchAvailableActivitiesForRange();
                              await fetchMonth();
                              setShowAvailableActivitiesModal(false);
                              setActivityDurations({}); // Clear durations
                            } catch (error) {
                              console.error('Error enrolling in activity:', error);
                              alert(`❌ Error: ${error.message}`);
                            }
                          }}
                          sx={{
                            bgcolor: '#E4C984',
                            color: '#171717',
                            '&:hover': {
                              bgcolor: '#d4b974'
                            },
                            minWidth: 140,
                            height: 'fit-content',
                            px: 3,
                            py: 1.5,
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            fontSize: '0.95rem'
                          }}
                        >
                          ENROLL
                        </Button>
                      </Box>

                      {/* Activity Details - 2 Column Layout */}
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, mb: 3 }}>
                        {/* Left Column - Time, Instructor, Location */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Time */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 20, color: '#E4C984' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                                TIME
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {activity.startTime} - {activity.endTime} ({defaultDuration} minutes)
                              </Typography>
                            </Box>
                          </Box>

                          {/* Instructor */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <PersonIcon sx={{ fontSize: 20, color: '#E4C984' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                                INSTRUCTOR
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {activity.instructorName}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Location */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <LocationOnIcon sx={{ fontSize: 20, color: '#E4C984' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                                LOCATION
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {activity.roomName}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Right Column - Enrolled and Booking Duration */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Enrolled */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <PeopleIcon sx={{ fontSize: 20, color: '#E4C984' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                                ENROLLED
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {activity.currentEnrollment}/{activity.maxCapacity}
                                </Typography>
                                {capacityPercent > 70 && (
                                  <Chip 
                                    label="HOT" 
                                    size="small"
                                    sx={{ 
                                      bgcolor: '#ef4444',
                                      color: '#fff',
                                      fontWeight: 700,
                                      fontSize: '0.7rem',
                                      height: 20,
                                      '& .MuiChip-label': {
                                        px: 1
                                      }
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Box>

                          {/* Booking Duration */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 20, color: '#E4C984', mt: 0.5 }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                                BOOKING DURATION (MINUTES)
                              </Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={activityDuration}
                                onChange={(e) => {
                                  const newDuration = parseInt(e.target.value) || 0;
                                  setActivityDurations(prev => ({
                                    ...prev,
                                    [activity.id]: newDuration
                                  }));
                                }}
                                inputProps={{ min: 15, max: defaultDuration, step: 15 }}
                                sx={{ width: 100, mb: 0.5 }}
                              />
                              <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.75rem' }}>
                                You can book up to <strong>{defaultDuration}</strong> minutes
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button 
              onClick={() => setShowAvailableActivitiesModal(false)}
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                textTransform: 'uppercase',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: '#9ca3af',
                  bgcolor: '#f9fafb'
                }
              }}
            >
              CLOSE
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Advanced Filter Panel - Drawer */}
      <Drawer
        anchor="left"
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 320,
            p: 2,
            bgcolor: '#fff'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeFilterCount > 0 && (
              <Button
                size="small"
                onClick={clearAllFilters}
                sx={{
                  color: '#E4C984',
                  '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.12)' }
                }}
              >
                Clear All
              </Button>
            )}
            <IconButton
              size="small"
              onClick={() => setFilterPanelOpen(false)}
            >
              <ClearIcon />
            </IconButton>
          </Box>
        </Box>

        {activeFilterCount > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {Object.entries(advancedFilters).map(([key, values]) =>
              values.map(value => (
                <Chip
                  key={`${key}-${value}`}
                  label={value}
                  size="small"
                  onDelete={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      [key]: prev[key].filter(v => v !== value)
                    }));
                  }}
                  sx={{
                    bgcolor: '#E4C984',
                    color: '#171717',
                    '& .MuiChip-deleteIcon': {
                      color: '#171717',
                      '&:hover': {
                        color: '#000'
                      }
                    }
                  }}
                />
              ))
            )}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Filter Categories */}
        <Box sx={{ overflow: 'auto' }}>
          {/* Gender Filter */}
          {filterOptions.gender.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 1,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
                onClick={() => setExpandedFilters(prev => ({ ...prev, gender: !prev.gender }))}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Gender
                </Typography>
                {expandedFilters.gender ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              <Collapse in={expandedFilters.gender}>
                <Box sx={{ pl: 1 }}>
                  {filterOptions.gender.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={advancedFilters.gender.includes(option)}
                          onChange={(e) => {
                            setAdvancedFilters(prev => ({
                              ...prev,
                              gender: e.target.checked
                                ? [...prev.gender, option]
                                : prev.gender.filter(v => v !== option)
                            }));
                          }}
                          sx={{
                            color: '#E4C984',
                            '&.Mui-checked': { color: '#E4C984' }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {option} ({getFilterCounts('gender', option)})
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* Guest Tag Filter */}
          <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 1,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
                onClick={() => setExpandedFilters(prev => ({ ...prev, guestTag: !prev.guestTag }))}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Guest Tag
                </Typography>
                {expandedFilters.guestTag ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              <Collapse in={expandedFilters.guestTag}>
                <Box sx={{ pl: 1 }}>
                  {filterOptions.guestTag.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={advancedFilters.guestTag.includes(option)}
                          onChange={(e) => {
                            setAdvancedFilters(prev => ({
                              ...prev,
                              guestTag: e.target.checked
                                ? [...prev.guestTag, option]
                                : prev.guestTag.filter(v => v !== option)
                            }));
                          }}
                          sx={{
                            color: '#E4C984',
                            '&.Mui-checked': { color: '#E4C984' }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {option} ({getFilterCounts('guestTag', option)})
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>

          {/* Package Filter */}
          {filterOptions.package.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 1,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
                onClick={() => setExpandedFilters(prev => ({ ...prev, package: !prev.package }))}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Package/Program
                </Typography>
                {expandedFilters.package ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              <Collapse in={expandedFilters.package}>
                <Box sx={{ pl: 1 }}>
                  {filterOptions.package.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={advancedFilters.package.includes(option)}
                          onChange={(e) => {
                            setAdvancedFilters(prev => ({
                              ...prev,
                              package: e.target.checked
                                ? [...prev.package, option]
                                : prev.package.filter(v => v !== option)
                            }));
                          }}
                          sx={{
                            color: '#E4C984',
                            '&.Mui-checked': { color: '#E4C984' }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {option} ({getFilterCounts('package', option)})
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* Age Range Filter */}
          {filterOptions.ageRange.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 1,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
                onClick={() => setExpandedFilters(prev => ({ ...prev, ageRange: !prev.ageRange }))}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Age Range
                </Typography>
                {expandedFilters.ageRange ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              <Collapse in={expandedFilters.ageRange}>
                <Box sx={{ pl: 1 }}>
                  {filterOptions.ageRange.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={advancedFilters.ageRange.includes(option)}
                          onChange={(e) => {
                            setAdvancedFilters(prev => ({
                              ...prev,
                              ageRange: e.target.checked
                                ? [...prev.ageRange, option]
                                : prev.ageRange.filter(v => v !== option)
                            }));
                          }}
                          sx={{
                            color: '#E4C984',
                            '&.Mui-checked': { color: '#E4C984' }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {option} ({getFilterCounts('ageRange', option)})
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* Contact Type Filter */}
          {filterOptions.contactType.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 1,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
                onClick={() => setExpandedFilters(prev => ({ ...prev, contactType: !prev.contactType }))}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Contact Type
                </Typography>
                {expandedFilters.contactType ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              <Collapse in={expandedFilters.contactType}>
                <Box sx={{ pl: 1 }}>
                  {filterOptions.contactType.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={advancedFilters.contactType.includes(option)}
                          onChange={(e) => {
                            setAdvancedFilters(prev => ({
                              ...prev,
                              contactType: e.target.checked
                                ? [...prev.contactType, option]
                                : prev.contactType.filter(v => v !== option)
                            }));
                          }}
                          sx={{
                            color: '#E4C984',
                            '&.Mui-checked': { color: '#E4C984' }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {option} ({getFilterCounts('contactType', option)})
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* Diet Filter */}
          {filterOptions.diet.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 1,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
                onClick={() => setExpandedFilters(prev => ({ ...prev, diet: !prev.diet }))}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Diet Type
                </Typography>
                {expandedFilters.diet ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              <Collapse in={expandedFilters.diet}>
                <Box sx={{ pl: 1 }}>
                  {filterOptions.diet.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={advancedFilters.diet.includes(option)}
                          onChange={(e) => {
                            setAdvancedFilters(prev => ({
                              ...prev,
                              diet: e.target.checked
                                ? [...prev.diet, option]
                                : prev.diet.filter(v => v !== option)
                            }));
                          }}
                          sx={{
                            color: '#E4C984',
                            '&.Mui-checked': { color: '#E4C984' }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {option} ({getFilterCounts('diet', option)})
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* Duration Filter */}
          {filterOptions.duration.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 1,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
                onClick={() => setExpandedFilters(prev => ({ ...prev, duration: !prev.duration }))}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Duration (Nights)
                </Typography>
                {expandedFilters.duration ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              <Collapse in={expandedFilters.duration}>
                <Box sx={{ pl: 1 }}>
                  {filterOptions.duration.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={advancedFilters.duration.includes(option)}
                          onChange={(e) => {
                            setAdvancedFilters(prev => ({
                              ...prev,
                              duration: e.target.checked
                                ? [...prev.duration, option]
                                : prev.duration.filter(v => v !== option)
                            }));
                          }}
                          sx={{
                            color: '#E4C984',
                            '&.Mui-checked': { color: '#E4C984' }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {option}N ({getFilterCounts('duration', option)})
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* Consulting Doctor Filter */}
          {filterOptions.consultingDoctor.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 1,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
                onClick={() => setExpandedFilters(prev => ({ ...prev, consultingDoctor: !prev.consultingDoctor }))}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Consulting Doctor
                </Typography>
                {expandedFilters.consultingDoctor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              <Collapse in={expandedFilters.consultingDoctor}>
                <Box sx={{ pl: 1 }}>
                  {filterOptions.consultingDoctor.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={advancedFilters.consultingDoctor.includes(option)}
                          onChange={(e) => {
                            setAdvancedFilters(prev => ({
                              ...prev,
                              consultingDoctor: e.target.checked
                                ? [...prev.consultingDoctor, option]
                                : prev.consultingDoctor.filter(v => v !== option)
                            }));
                          }}
                          sx={{
                            color: '#E4C984',
                            '&.Mui-checked': { color: '#E4C984' }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {option} ({getFilterCounts('consultingDoctor', option)})
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Itinerary Modal */}
      <ItineraryModal
        open={showItinerary}
        onClose={() => setShowItinerary(false)}
        guestId={selectedGuest?.id || selectedGuest?.guestId || lockedGuestId}
        guestName={selectedGuest?.name || selectedGuest?.guestName || lockedGuestName}
        bookingId={selectedGuest?.bookingId || selectedGuest?.booking_id}
      />
    </Box>
  );
};

export default DoctorDesk;


